import { ClusterDiscoveryStatus, ClusterType, EarlyAccessFeature } from "@doitintl/cmp-models";
import { CircularProgress } from "@mui/material";

import { ThreeDotsMenu, type ThreeDotsMenuOption } from "../../../../Components/ThreeDotsMenu";
import { useAuthContext } from "../../../../Context/AuthContext";
import { useCustomerContext } from "../../../../Context/CustomerContext";
import { CLUSTER_PAGE_MESSAGES } from "../../messages";
import { ClusterAgentStatus, type ClusterTableItem } from "./types";

interface ClusterThreeDotsMenuProps {
  cluster: ClusterTableItem;
  onTestConnection?: () => Promise<void>;
  onEnableFeatures?: () => Promise<void>;
  onDeactivateCluster?: () => Promise<void>;
  onDeleteCluster?: () => void;
  onEditCluster?: () => void;
  onInstallAgent?: () => void;
  onUpgradeAgent?: () => void;
  loading?: boolean;
}

type ExtendedThreeDotsMenuOption = ThreeDotsMenuOption & {
  featureFlag?: EarlyAccessFeature;
};

const ClusterThreeDotsMenu = ({
  cluster,
  onTestConnection,
  onEnableFeatures,
  onDeactivateCluster,
  onDeleteCluster,
  onEditCluster,
  onInstallAgent,
  onUpgradeAgent,
  loading = false,
}: ClusterThreeDotsMenuProps) => {
  const { customer } = useCustomerContext();
  const { isDoitEmployee } = useAuthContext();

  const clusterIsDeleted = cluster.discoveryStatus === ClusterDiscoveryStatus.DELETED;

  const getOptionParameters = () => {
    if (clusterIsDeleted) {
      return {
        tooltip: CLUSTER_PAGE_MESSAGES.DELETED_CLUSTER,
        disabled: true,
      };
    }

    if (cluster.isPrivate) {
      return {
        tooltip: CLUSTER_PAGE_MESSAGES.PRIVATE_CLUSTER,
        disabled: true,
      };
    }

    return {
      disabled: false,
    };
  };

  const options: ExtendedThreeDotsMenuOption[] = [
    {
      ...getOptionParameters(),
      label: "Enable features",
      key: "enable-features",
      action: onEnableFeatures,
    },
  ];

  if (cluster.isActivated) {
    options.push(
      ...[
        {
          ...getOptionParameters(),
          label: "Deactivate",
          key: "deactivate-cluster",
          action: onDeactivateCluster,
        },
        {
          ...getOptionParameters(),
          label: "Test connection",
          key: "test-cluster-connection",
          action: onTestConnection,
        },
      ]
    );
  }

  if (cluster.agentStatus === ClusterAgentStatus.UPGRADE_AVAILABLE) {
    options.push({
      label: "Upgrade agent",
      key: "upgrade-agent",
      action: onUpgradeAgent,
      featureFlag: EarlyAccessFeature.FSK8S,
    });
  }

  if (cluster.agentStatus === ClusterAgentStatus.NOT_INSTALLED) {
    options.push({
      label: "Install agent",
      key: "install-agent",
      action: onInstallAgent,
      featureFlag: EarlyAccessFeature.FSK8S,
    });
  }

  if (cluster.type === ClusterType.SELF_MANAGED) {
    options.push(
      {
        label: "Delete",
        key: "delete-cluster",
        action: onDeleteCluster,
      },
      {
        label: "Edit",
        key: "edit-cluster",
        action: onEditCluster,
      }
    );
  }

  options.forEach((option) => {
    option.tooltipPlacement = "left";
    option.dataCy = option.dataCy ?? option.key;
  });

  const availableOptions: ThreeDotsMenuOption[] = options.filter(
    (option) => !option.featureFlag || isDoitEmployee || customer.earlyAccessFeatures?.includes(option.featureFlag)
  );

  if (loading) {
    return <CircularProgress size={24} />;
  }

  return <ThreeDotsMenu options={availableOptions} size="small" closeAfterSelect />;
};

export default ClusterThreeDotsMenu;
